import React from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CURRENT_LEVELS } from '../../../utils/studyPlanMockData';

interface CurrentLevelStepProps {
  onNext: (data: { currentLevel: string; currentScore?: number }) => void;
  onBack: () => void;
  examType?: string;
  defaultValue?: { currentLevel: string; currentScore?: number };
}

interface FormData {
  currentLevel: string;
  currentScore?: number;
}

const CurrentLevelStep: React.FC<CurrentLevelStepProps> = ({ onNext, onBack, examType, defaultValue }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormData>({
    defaultValues: { 
      currentLevel: defaultValue?.currentLevel || '',
      currentScore: defaultValue?.currentScore || undefined
    }
  });

  const selectedLevel = watch('currentLevel');
  const currentScore = watch('currentScore');

  const getMaxScore = () => {
    switch (examType) {
      case 'neet': return 720;
      case 'jee-mains': return 300;
      case 'jee-advance': return 372;
      default: return 720;
    }
  };

  const getExamName = () => {
    switch (examType) {
      case 'neet': return 'NEET';
      case 'jee-mains': return 'JEE Mains';
      case 'jee-advance': return 'JEE Advanced';
      default: return 'Exam';
    }
  };

  const onSubmit = (data: FormData) => {
    onNext(data);
  };

  const getLevelIcon = (levelId: string) => {
    switch (levelId) {
      case 'beginner':
        return 'fa-seedling';
      case 'exam-attempted':
        return 'fa-redo-alt';
      case 'exam-qualified':
        return 'fa-chart-line';
      default:
        return 'fa-user';
    }
  };

  const getLevelColor = (levelId: string) => {
    switch (levelId) {
      case 'beginner':
        return 'success';
      case 'exam-attempted':
        return 'warning';
      case 'exam-qualified':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="text-center mb-5">
              <h2 className="display-6 fw-bold text-primary mb-3">
                What's Your Current Level?
              </h2>
              <p className="lead text-muted">
                Help us understand where you stand in your preparation journey
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-4 mb-5">
                {CURRENT_LEVELS.map((level) => (
                  <div key={level.id} className="col-12">
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      className={`card border-2 cursor-pointer position-relative ${
                        selectedLevel === level.id 
                          ? `border-${getLevelColor(level.id)} bg-light shadow-sm` 
                          : 'border-light hover-shadow'
                      }`}
                      onClick={() => {
                        const input = document.getElementById(`level-${level.id}`) as HTMLInputElement;
                        if (input) input.click();
                      }}
                    >
                      <div className="card-body p-4">
                        {selectedLevel === level.id && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <div className={`bg-${getLevelColor(level.id)} text-white rounded-circle d-flex align-items-center justify-content-center`} 
                                 style={{ width: '24px', height: '24px' }}>
                              <i className="fas fa-check" style={{ fontSize: '12px' }}></i>
                            </div>
                          </div>
                        )}
                        <div className="row align-items-center">
                          <div className="col-auto">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center ${
                              selectedLevel === level.id 
                                ? `bg-${getLevelColor(level.id)} text-white` 
                                : `bg-${getLevelColor(level.id)} bg-opacity-20 text-${getLevelColor(level.id)}`
                            }`} style={{ width: '60px', height: '60px' }}>
                              <i className={`fas ${getLevelIcon(level.id)} fa-lg`}></i>
                            </div>
                          </div>
                          
                          <div className="col">
                            <h5 className={`mb-2 fw-bold ${
                              selectedLevel === level.id ? `text-${getLevelColor(level.id)}` : 'text-dark'
                            }`}>
                              {level.name}
                            </h5>
                            <p className={`mb-0 ${
                              selectedLevel === level.id ? 'text-dark' : 'text-muted'
                            }`}>
                              {level.description}
                            </p>
                          </div>
                          
                          <div className="col-auto">
                            <div className="form-check">
                              <input
                                {...register('currentLevel', { required: 'Please select your current level' })}
                                className="form-check-input"
                                type="radio"
                                value={level.id}
                                id={`level-${level.id}`}
                                style={{ pointerEvents: 'none' }}
                              />
                              <label className="form-check-label" htmlFor={`level-${level.id}`}></label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>

              {/* Score Input Section */}
              {(selectedLevel === 'exam-attempted' || selectedLevel === 'exam-qualified') && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-5"
                >
                  <div className="card border-info border-2">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-calculator me-2"></i>
                        Enter Your Current Score
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            {selectedLevel === 'exam-attempted' ? 'Previous Attempt Score' : 'Your Current Score'}
                          </label>
                          <div className="input-group input-group-lg">
                            <input
                              {...register('currentScore', { 
                                required: 'Please enter your score',
                                min: { value: 0, message: 'Score cannot be negative' },
                                max: { value: getMaxScore(), message: `Score cannot exceed ${getMaxScore()}` }
                              })}
                              type="number"
                              className="form-control"
                              placeholder="Enter your score"
                              min="0"
                              max={getMaxScore()}
                            />
                            <span className="input-group-text">/ {getMaxScore()}</span>
                          </div>
                          <small className="text-muted">
                            Enter your {getExamName()} score from your {selectedLevel === 'exam-attempted' ? 'previous attempt' : 'last exam'}
                          </small>
                        </div>
                        <div className="col-md-6">
                          <div className="bg-light p-3 rounded">
                            <h6 className="text-primary mb-2">
                              <i className="fas fa-info-circle me-2"></i>
                              Score Guidelines
                            </h6>
                            <ul className="small mb-0">
                              <li><strong>NEET:</strong> Max 720 marks</li>
                              <li><strong>JEE Mains:</strong> Max 300 marks</li>
                              <li><strong>JEE Advanced:</strong> Max 372 marks</li>
                            </ul>
                            <div className="mt-2">
                              <small className="text-success">
                                <i className="fas fa-info-circle me-1"></i>
                                Current exam: <strong>{getExamName()}</strong> (Max: {getMaxScore()} marks)
                              </small>
                              <br />
                              <small className="text-muted">
                                This helps us create a more targeted study plan for your improvement.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {errors.currentLevel && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {errors.currentLevel.message}
                </div>
              )}

              {errors.currentScore && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {errors.currentScore.message}
                </div>
              )}

              <div className="d-flex justify-content-between">
                <button
                  type="button"
                  onClick={onBack}
                  className="btn btn-outline-secondary btn-lg px-4"
                >
                  <i className="fas fa-arrow-left me-2"></i>
                  Back
                </button>
                
                <button
                  type="submit"
                  className="btn btn-primary btn-lg px-5"
                  disabled={!selectedLevel || 
                    ((selectedLevel === 'exam-attempted' || selectedLevel === 'exam-qualified') && !currentScore)
                  }
                >
                  Continue
                  <i className="fas fa-arrow-right ms-2"></i>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CurrentLevelStep;